import { useEffect, useState } from 'react';

import { Button, XmarkIcon } from '../../index';
import { addStyles } from '../../utils/helpers';

export interface ChipProps {
  /** The value of the chip */
  value: string;
  /** Whether the chip is selected */
  isSelected: boolean;
  /** The onChange handler */
  onChange: (selected: boolean) => void;
  /** An optional icon to display to the left of the chip */
  icon?: React.FunctionComponent<
    React.SVGProps<SVGSVGElement> & {
      title?: string | undefined;
    }
  >;
}

const options = {
  container: {
    base: 'flex flex-nowrap items-center box-border rounded-lg border-solid border h-min w-fit cursor-pointer pl-5',
    unselected:
      'border-gray-600 bg-white text-gray-900 pr-5 py-2.5 hover:text-primary hover:fill-primary active:border-primary',
    selected: 'border-jungle-600 bg-jungle-50 text-jungle-900 fill-jungle-900',
  },
  text: {
    base: 'text-base select-none',
    selected: 'pr-1',
    unselected: '',
  },
  icon: {
    base: 'h-6 w-6',
    selected: '',
    unselected: '',
  },
};

const getState = (isSelected: boolean): 'selected' | 'unselected' => {
  return isSelected ? 'selected' : 'unselected';
};

export const Chip = (props: ChipProps) => {
  const { value, isSelected, onChange, icon } = props;
  const [selectedState, setSelected] = useState<'selected' | 'unselected'>(getState(isSelected));

  const chipSelected = () => {
    return selectedState === 'selected';
  };

  const onClick = () => {
    if (!chipSelected()) {
      onChange(true);
      setSelected('selected');
    }
  };

  const onClear = () => {
    onChange(false);
    setSelected('unselected');
  };

  useEffect(() => {
    setSelected(getState(isSelected));
  }, [isSelected]);

  return (
    <div className={addStyles([options.container.base, options.container[selectedState]])} onClick={onClick}>
      {icon && (
        <div className="pr-2">
          {icon({
            className: addStyles([options.icon.base, options.icon[selectedState]]),
          })}
        </div>
      )}
      <span className={addStyles([options.text.base, options.text[selectedState]])}>{value}</span>
      {chipSelected() && <Button icon={XmarkIcon} onClick={onClear} type="action" />}
    </div>
  );
};
export default Chip;
